import { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import OutOfStock from 'components/gruenland/atoms/badge/OutOfStock';
import Promo from 'components/gruenland/atoms/badge/Promo';
import Image from 'components/gruenland/atoms/image';
import Link from 'components/gruenland/atoms/link';
import useShopable from 'helpers/hooks/useShopable';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { sendSelectItemEvent } from 'helpers/utils/ga4';
import { TYPOGRAPHY_PRODUCT_CARD_DESCRIPTION, TYPOGRAPHY_PRODUCT_CARD_TITLE } from 'lib/typography';
import { productTileImage } from 'lib/wardaImages';
import { Product, Variant } from 'types/entity/product';
import { LineItem, Wishlist } from 'types/entity/wishlist';
import ColorGroupCodeVariantPickerDot from './ColorGroupCodeVariantPickerDot';
import Prices from './prices';
import WishlistButton from '../wishlist/WishlistButton';

export interface ProductTileProps {
  variant?: 'plp' | 'fullWidthSlider';
  product: Product;
  isSearchResult?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  selectedVariantIndex?: number;
  wishlist?: Wishlist;
  cta?: React.ReactNode;
  onClick?: () => void;
  removeLineItem?: (item: LineItem) => Promise<void>;
  addToWishlist?: (lineItem: LineItem, count: number) => Promise<void>;
}

const ProductTile: React.FC<ProductTileProps> = ({
  variant = 'plp',
  product,
  isSearchResult = false,
  disableWishlistButton = false,
  disableVariants = false,
  selectedVariantIndex = 0,
  wishlist,
  cta,
  onClick,
  addToWishlist,
  removeLineItem,
}) => {
  // const [isDesktopSize] = useMediaQuery(desktop);

  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;

  const [userSelectedVariant, setUserSelectedVariant] = useState<Variant | undefined>(undefined);

  const selectedVariant = useMemo(
    () => userSelectedVariant ?? variantWithDiscount ?? product?.variants[selectedVariantIndex],
    [product, selectedVariantIndex, userSelectedVariant, variantWithDiscount],
  );

  const hasDiscount = useMemo(
    () =>
      selectedVariant.discountedPrice?.centAmount &&
      selectedVariant.price?.centAmount &&
      selectedVariant.discountedPrice.centAmount < selectedVariant.price.centAmount,
    [selectedVariant],
  );
  const discountedPrice = selectedVariant?.discountedPrice;

  /* const discountPercentage = selectedVariant
    ? Math.round(
        (((selectedVariant.price?.centAmount as number) - (discountedPrice?.centAmount as number)) /
          (selectedVariant.price?.centAmount as number)) *
          100,
      )
    : 0; */

  // const [imageHovered, setImageHovered] = useState(false);
  // const [buttonHovered, setButtonHovered] = useState(false);
  /* const buttonIsVisible = useMemo(
    () => (imageHovered || buttonHovered) && isDesktopSize,
    [imageHovered, buttonHovered, isDesktopSize],
  );

  const hideButton = () => {
    setImageHovered(false);
    setButtonHovered(false);
  }; */

  const productToWishlistLineItem = useMemo<LineItem | undefined>(() => {
    if (product) {
      return {
        lineItemId: product.productId ?? '',
        productId: product.productId,
        productKey: product.productKey,
        productRef: product.productRef,
        name: product.name,
        count: 1,
        variant: selectedVariant,
        addedAt: new Date(),
        _url: product._url,
      };
    }
  }, [product, selectedVariant]);

  const productUrl = useMemo(() => {
    if (!product._url) return `/${(product.name ?? '').toLowerCase().replace(/\s+/g, '-')}/p/${selectedVariant.sku}`;
    const name = product._url.split('/')[1];
    return `/${name}/p/${selectedVariant.sku}` + (isSearchResult ? '?sr=1' : '');
  }, [product, selectedVariant, isSearchResult]);

  const shopable = useShopable();

  const handlePickVariant = useCallback<React.ComponentProps<typeof ColorGroupCodeVariantPickerDot>['onClick']>(
    ({ index, variant }) => {
      sendSelectItemEvent(product, variant, index);
      setUserSelectedVariant(variant);
    },
    [product],
  );

  const variants = useMemo(
    () =>
      product?.variants.filter(
        (variant, index, arr) =>
          arr.findIndex((v) => v.attributes?.colourGroupCode === variant.attributes?.colourGroupCode) === index,
      ) || [],
    [product],
  );

  return (
    <div onClick={onClick} className="relative flex w-full flex-col items-center justify-between bg-white">
      <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between">
        <div className="flex gap-2">
          {hasDiscount && <Promo />}
          {!selectedVariant.isOnStock && <OutOfStock />}
        </div>

        {!disableWishlistButton && productToWishlistLineItem && (
          <WishlistButton
            data={wishlist}
            lineItem={productToWishlistLineItem}
            className="size-6 lg:size-[30px]"
            addToWishlist={addToWishlist}
            removeFromWishlist={removeLineItem}
          />
        )}
      </div>

      <Link
        link={productUrl}
        data-testid="image-container"
        className={classNames('relative block w-full', {
          'h-[150px] sm:h-[138px] md:h-[203px] lg:h-[193px] xl:h-[251px] 2xl:h-[210px] 3xl:h-[247px]':
            variant === 'plp',
          'h-[200px] md:h-[250px] lg:h-[300px] xl:h-[350px] 2xl:h-[400px] 3xl:h-[580px]': variant === 'fullWidthSlider',
        })}
      >
        <Image
          src={productTileImage(selectedVariant.images)}
          alt={product.name ?? ''}
          style={{ objectFit: 'contain', objectPosition: 'center' }}
          className="w-full"
          fill
          // suffix="medium"
        />
      </Link>
      <div className="w-full">
        {!disableVariants && (
          <div
            className={classNames('mt-2 w-full items-center gap-3 md:mt-3', {
              hidden: disableVariants,
              flex: !disableVariants,
            })}
          >
            {variants.map((variant, index) => (
              <ColorGroupCodeVariantPickerDot
                key={variant.sku}
                colourGroupCode={variant.attributes?.colourGroupCode}
                active={variant.sku === selectedVariant.sku}
                variant={variant}
                index={index}
                onClick={handlePickVariant}
              />
            ))}
          </div>
        )}
        <p className={classNames(TYPOGRAPHY_PRODUCT_CARD_TITLE, 'mt-5')}>
          {selectedVariant?.attributes?.structure} {product?.productKey}
        </p>
        <p className={TYPOGRAPHY_PRODUCT_CARD_DESCRIPTION}>{product?.name}</p>
        {shopable && (
          <div className="mt-2 md:mt-3">
            <Prices price={selectedVariant?.price} discountedPrice={discountedPrice} />
          </div>
        )}
        {shopable && cta && <div className="mt-2 flex items-center justify-center">{cta}</div>}
      </div>
    </div>
  );
};

export default ProductTile;
